// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — bootstrap and the screen machine. Owns which screen is up, the room's Net (created
// ONCE per session, never left for a rematch), and the rAF draw loop that ticks the clock and
// draws. rAF only DRAWS + advances the clock (timestamp-driven), so a backgrounded tab never
// strands a round. Rules live in game.ts, the board in render.ts, the race in race.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { makeRng } from '@ben-gy/game-engine/rng';

import { escapeHtml, fmtScore, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import {
  createGame,
  drainEvents,
  paint as paintGame,
  progress,
  tick as tickGame,
  timeLeftMs,
  UNKNOWN,
  type GameEvent,
  type GameState,
  type PaintAction,
} from './game';
import { dailySeed, newSeed, seedToCode, todayUTC } from './seed';
import { Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown } from './countdown';
import { nextMove, policyOf, READER } from './bot';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'hatch';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
const playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number {
  return store.get<number>(bestKey(id), 0) ?? 0;
}
function recordBest(id: string, score: number): boolean {
  const cur = bestFor(id);
  if (score > cur) {
    store.set(bestKey(id), score);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  tick: (dtMs: number) => GameEvent[];
  paint: (idx: number, action: PaintAction) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; score: number; finished: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// Exposed only so the browser-verification hook can drive the REAL input→core path.
let activeBoard: Board | null = null;
let activePaint: ((idx: number, action: PaintAction) => void) | null = null;
let activeState: (() => GameState) | null = null;

function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'fill') sfx.play('coin', { pitch: 0.55 + Math.min(1, (e.run ?? 1) / 8) * 0.9 });
      else if (e.t === 'mistake') sfx.play('lose');
      else if (e.t === 'mark') sfx.play('select', { pitch: 0.9 });
      else if (e.t === 'linedone') sfx.play('beat', { pitch: 1.2 });
      else if (e.t === 'complete') sfx.play('go');
      else if (e.t === 'over') sfx.play(e.won ? 'go' : 'explosion');
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'fill') navigator.vibrate(6);
        else if (e.t === 'mistake') navigator.vibrate([28, 40, 28]);
        else if (e.t === 'complete') navigator.vibrate([12, 30, 12, 30, 40]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-score" id="hudScore">0</span>
        <span class="hud-extra" id="hudExtra"></span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
        <div class="announce" id="announce" hidden></div>
      </div>
      <div class="toolbar" id="toolbar" role="group" aria-label="Tool">
        <button class="toolbtn on" id="toolFill" data-tool="fill" aria-pressed="true"><span class="tb-ico">✎</span> Fill</button>
        <button class="toolbtn" id="toolMark" data-tool="mark" aria-pressed="false"><span class="tb-ico">✕</span> Mark</button>
      </div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudScore = content.querySelector<HTMLElement>('#hudScore')!;
  const hudExtra = content.querySelector<HTMLElement>('#hudExtra')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  const announce = content.querySelector<HTMLElement>('#announce')!;
  const toolFill = content.querySelector<HTMLButtonElement>('#toolFill')!;
  const toolMark = content.querySelector<HTMLButtonElement>('#toolMark')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  activeBoard = board;
  activePaint = (idx, action) => applyPaint(idx, action);
  activeState = cfg.state;
  const doResize = (): void => board.resize();
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  let tool: 'fill' | 'mark' = 'fill';
  function setTool(t: 'fill' | 'mark'): void {
    tool = t;
    toolFill.classList.toggle('on', t === 'fill');
    toolMark.classList.toggle('on', t === 'mark');
    toolFill.setAttribute('aria-pressed', String(t === 'fill'));
    toolMark.setAttribute('aria-pressed', String(t === 'mark'));
  }
  toolFill.addEventListener('click', () => setTool('fill'));
  toolMark.addEventListener('click', () => setTool('mark'));

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;
  let hudTick = 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    last = performance.now();
  });

  function applyPaint(idx: number, action: PaintAction): GameEvent[] {
    if (destroyed || !live || paused || cfg.isOver()) return [];
    const events = cfg.paint(idx, action);
    board.onEvents(events, reducedMotion);
    juice(events);
    for (const e of events) {
      if (e.t === 'complete' && mode.stream) flashAnnounce('Nice!');
    }
    return events;
  }

  function flashAnnounce(msg: string): void {
    announce.textContent = msg;
    announce.hidden = false;
    announce.classList.remove('pop');
    void announce.offsetWidth;
    announce.classList.add('pop');
    setTimeout(() => {
      if (!destroyed) announce.hidden = true;
    }, 700);
  }

  // ── grid paint input (pointer events; per-pointer stroke; stop a fill-stroke on a mistake) ──
  let strokeId: number | null = null;
  let strokePaint: PaintAction | null = null; // for a stroke, the locked action
  let strokeStopped = false;
  let lastRC: { r: number; c: number } | null = null;
  const paintedThisStroke = new Set<number>();
  const size = (): number => cfg.state().board.size;

  function paintCell(idx: number): void {
    if (strokeStopped || paintedThisStroke.has(idx)) return;
    paintedThisStroke.add(idx);
    const before = cfg.state().mistakes;
    applyPaint(idx, strokePaint!);
    if (strokePaint === 'fill' && cfg.state().mistakes > before) strokeStopped = true; // one mistake ends a fill-drag
  }

  function paintTo(r: number, c: number): void {
    if (!lastRC) {
      paintCell(r * size() + c);
      lastRC = { r, c };
      return;
    }
    // walk a straight grid line from lastRC to (r,c) so a fast drag leaves no gaps
    let { r: r0, c: c0 } = lastRC;
    const dr = Math.sign(r - r0);
    const dc = Math.sign(c - c0);
    const steps = Math.max(Math.abs(r - r0), Math.abs(c - c0));
    for (let i = 0; i < steps; i++) {
      // step along the dominant axis, then the other, one cell at a time
      if (Math.abs(r - r0) >= Math.abs(c - c0) && r0 !== r) r0 += dr;
      else if (c0 !== c) c0 += dc;
      else if (r0 !== r) r0 += dr;
      if (r0 >= 0 && r0 < size() && c0 >= 0 && c0 < size()) paintCell(r0 * size() + c0);
      if (strokeStopped) break;
    }
    lastRC = { r, c };
  }

  boardHost.addEventListener('pointerdown', (e) => {
    if (!live || paused || cfg.isOver()) return;
    const idx = board.hitTest(e.clientX, e.clientY);
    if (idx === null) return;
    e.preventDefault();
    try {
      boardHost.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
    strokeId = e.pointerId;
    strokeStopped = false;
    paintedThisStroke.clear();
    const cell = cfg.state().cells[idx];
    // lock the stroke's action from the first cell
    if (tool === 'fill') strokePaint = 'fill';
    else strokePaint = cell === 2 ? 'unmark' : 'mark';
    lastRC = null;
    board.cursor = -1;
    paintTo(Math.floor(idx / size()), idx % size());
  });

  boardHost.addEventListener('pointermove', (e) => {
    if (strokeId !== e.pointerId || strokeStopped) return;
    const idx = board.hitTest(e.clientX, e.clientY);
    if (idx === null) return;
    paintTo(Math.floor(idx / size()), idx % size());
  });

  function endStroke(e: PointerEvent): void {
    if (strokeId !== e.pointerId) return;
    strokeId = null;
    strokePaint = null;
    lastRC = null;
    paintedThisStroke.clear();
    try {
      boardHost.releasePointerCapture(e.pointerId);
    } catch {
      /* best-effort */
    }
  }
  boardHost.addEventListener('pointerup', endStroke);
  boardHost.addEventListener('pointercancel', endStroke);

  function updateHud(state: GameState): void {
    hudScore.textContent = fmtScore(state.score);
    if (mode.clock === 'down') {
      hudExtra.innerHTML = `<span class="hud-time">${fmtTime(timeLeftMs(state))}</span> · <span class="hud-pics">🖼 ${state.banked}</span>`;
    } else if (mode.maxStrikes > 0) {
      let pips = '';
      for (let i = 0; i < mode.maxStrikes; i++) pips += `<span class="pip${i < state.strikes ? ' out' : ''}"></span>`;
      hudExtra.innerHTML = `<span class="strikes">${pips}</span>`;
    } else {
      hudExtra.innerHTML = `<span class="hud-prog">${Math.round(progress(state) * 100)}%</span>`;
    }

    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => `<span class="rival${r.finished ? ' done' : ''}">${escapeHtml(r.name)} <b>${fmtScore(r.score)}</b></span>`)
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(80, now - last);
    last = now;
    const state = cfg.state();
    if (live && !paused && !cfg.isOver()) {
      const events = cfg.tick(dt);
      board.onEvents(events, reducedMotion);
      juice(events);
    }
    board.frame(state, { dtMs: dt, reducedMotion });
    if (++hudTick % 2 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 780);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (paused || cfg.isOver()) {
      if (e.key === 'p' || e.key === 'P') setPaused(!paused);
      return;
    }
    if (!live) return;
    if (e.key === 'ArrowRight' || e.key === 'd') board.moveCursor(0, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'a') board.moveCursor(0, -1);
    else if (e.key === 'ArrowUp' || e.key === 'w') board.moveCursor(-1, 0);
    else if (e.key === 'ArrowDown' || e.key === 's') board.moveCursor(1, 0);
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      applyPaint(board.cursorIdx(), 'fill');
    } else if (e.key === 'x' || e.key === 'X') {
      const idx = board.cursorIdx();
      applyPaint(idx, cfg.state().cells[idx] === 2 ? 'unmark' : 'mark');
    } else if (e.key === 'f' || e.key === 'F') {
      setTool('fill');
    } else if (e.key === 'm' || e.key === 'M') {
      setTool('mark');
    } else if (e.key === 'p' || e.key === 'P') {
      setPaused(!paused);
    } else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) {
      cfg.onRestart?.();
    }
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      if (activeBoard === board) {
        activeBoard = null;
        activePaint = null;
        activeState = null;
      }
      cancelAnimationFrame(raf);
      cd.cancel();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    tick: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    paint: (idx, action) => {
      paintGame(soloState!, idx, action);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => startSolo(soloDaily),
  });
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const isNewBest = recordBest(st.mode.id, st.score);
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({ state: st, best: bestFor(mode.id), isNewBest, daily: soloDaily })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${st.score > 0 ? 'score' : 'this board'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#rfeedback')?.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Hatch ${todayUTC()} · ${mode.name}` : `Hatch · ${mode.name}`;
  const line = st.score > 0 ? `scored ${fmtScore(st.score)}` : `couldn't crack this ${mode.name} board`;
  const link = soloDaily ? 'https://hatch.benrichardson.dev' : `https://hatch.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

async function beginFriends(): Promise<void> {
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same board. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 800);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    tick: (dt) => rc.tick(dt),
    paint: (idx, action) => rc.paint(idx, action),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, score: p.score, finished: p.finished })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().find((r) => !r.left);
    if (top && top.score > 0) tally[top.seat] = (tally[top.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.finished && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still solving: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    void beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

function startSeed(seed: number): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    tick: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    paint: (idx, action) => {
      paintGame(soloState!, idx, action);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => startSolo(false),
  });
  onCleanup(() => soloScreen?.destroy());
}

// The browser-verification pass drives this to prove the input→core path and that a bot can
// COMPLETE the objective, WITHOUT a human (rAF is throttled in a hidden tab).
(window as unknown as { __hatch?: unknown }).__hatch = {
  state(): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    return {
      score: s.score,
      mistakes: s.mistakes,
      strikes: s.strikes,
      correctFills: s.correctFills,
      filled: s.board.filledCount,
      banked: s.banked,
      over: s.over,
      won: s.won,
      mode: s.mode.id,
      size: s.board.size,
      progress: progress(s),
    };
  },
  // Client position of a cell that is truly filled and still unknown — so the browser test can
  // dispatch a REAL click there and prove the fill geometry + scoring.
  fillablePos(): { x: number; y: number } | null {
    const s = activeState?.() ?? soloState ?? race?.state;
    if (!s || !activeBoard) return null;
    for (let i = 0; i < s.cells.length; i++) {
      if (s.cells[i] === UNKNOWN && s.board.solution[i] === 1) return activeBoard.cellCenterClient(i);
    }
    return null;
  },
  // Client position of a cell that is truly EMPTY and unknown — to prove the mistake path.
  emptyPos(): { x: number; y: number } | null {
    const s = activeState?.() ?? soloState ?? race?.state;
    if (!s || !activeBoard) return null;
    for (let i = 0; i < s.cells.length; i++) {
      if (s.cells[i] === UNKNOWN && s.board.solution[i] === 0) return activeBoard.cellCenterClient(i);
    }
    return null;
  },
  fillNextTrue(): void {
    const s = activeState?.() ?? soloState ?? race?.state;
    if (!s || !activePaint) return;
    for (let i = 0; i < s.cells.length; i++) {
      if (s.cells[i] === UNKNOWN && s.board.solution[i] === 1) {
        activePaint(i, 'fill');
        return;
      }
    }
  },
  // Drive the CURRENT game synchronously with a bot for `ms` (rAF is throttled in a hidden tab).
  botRun(ms: number, policyId = READER.id): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const policy = policyOf(policyId);
    const rng = makeRng(0x51de);
    const cap = s.elapsedMs + ms;
    let guard = 0;
    while (!s.over && s.elapsedMs < cap && guard++ < 200_000) {
      const mv = nextMove(s, policy, rng);
      if (mv) {
        if (soloState) paintGame(soloState, mv.idx, mv.action);
        else race?.paint(mv.idx, mv.action);
      }
      if (soloState) tickGame(soloState, 60);
      else race?.tick(60);
    }
    return { score: s.score, mistakes: s.mistakes, over: s.over, won: s.won, banked: s.banked };
  },
};

boot();
